import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, CardHeader, Divider, Grid,
  TextField, Container, Typography, InputAdornment, IconButton, Stack,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { BASE_URL } from 'config/constant';
import axios from 'axios';
import { findUserId, getToken, getUserId } from '../Service/Service';
import { AccountProfile } from 'features/account/AccountProfile';
import { useAppSelector } from 'app/hooks';
import { selectAuth } from 'app/services/auth/authSlice';
import toast from 'react-hot-toast';

export const AccountPage = (props: any) => {
  const auth = useAppSelector(selectAuth);

  const [user, setUser] = useState({
    user_id: findUserId(),
    name: '',
    city: '',
    dob: '',
    email: '',
    mobile: '',
    status: '',
    profile_url: '',
    last_login_timestamp: '',
  });

  const [profile, setProfile] = useState({ preview: '', raw: '' as any });

  const [pwData, setPwData] = useState({
    old_password: '',
    new_password: '',
    confrim_password: '',
  });

  const [showPw, setShowPw] = useState({ old: false, new: false, confirm: false });

  React.useEffect(() => {
    document.title = 'Jaman HP Gas | Profile';
    fetchUser();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPwData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const fetchUser = async () => {
    try {
      const result = await axios.post(
        BASE_URL + 'user/find',
        { user_id: getUserId() },
        { headers: { encryption: false, token: getToken() } }
      );
      if (result.data?.data) {
        setUser(result.data.data);
        localStorage.setItem('jhpuser', JSON.stringify(result.data.data));
      } else {
        toast.error(result.data?.message || 'Failed to load profile');
      }
    } catch {
      toast.error('Unable to find user!');
    }
  };

  const HandleUpdate = async () => {
    try {
      const result = await axios.post(
        BASE_URL + 'user/update',
        {
          user_id: user.user_id,
          name: user.name,
          email: user.email,
          dob: user.dob,
          city: user.city,
          mobile: user.mobile,
          status: user.status,
        },
        { headers: { encryption: false, token: getToken() } }
      );
      if (result.data) {
        toast.success('Profile updated successfully!');
        await fetchUser();
      }
    } catch {
      toast.error("Profile couldn't update");
    }
  };

  const updatePassword = async () => {
    if (!pwData.old_password || !pwData.new_password || !pwData.confrim_password) {
      return toast.error('Please fill in all password fields');
    }
    if (pwData.new_password !== pwData.confrim_password) {
      return toast.error("New password and confirm password didn't match!");
    }
    try {
      const result = await axios.post(
        BASE_URL + 'user/changepassword',
        {
          user_id: getUserId(),
          old_password: pwData.old_password,
          new_password: pwData.new_password,
        },
        { headers: { encryption: false, token: getToken() } }
      );
      if (result.data) {
        toast.success(result.data.message);
        handleLogout();
      }
    } catch {
      toast.error('Invalid password!');
    }
  };

  const HandlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    if (!file.name.match(/\.(jpg|jpeg|png)$/i)) {
      return toast.error('Only .jpg and .png files are supported');
    }
    if (file.size > 1000089) {
      return toast.error('Please upload a file smaller than 1 MB');
    }
    setProfile({ preview: URL.createObjectURL(file), raw: file });
  };

  const UploadPhoto = async (e: React.MouseEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('image', profile.raw);
    formData.append('user_id', getUserId());
    try {
      const res = await fetch(BASE_URL + 'user/uploadprofilephoto', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      toast.success(data.message);
      setProfile({ preview: '', raw: '' });
      await fetchUser();
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(
        BASE_URL + 'user/logout',
        {},
        { headers: { encryption: false, token: getToken() } }
      );
    } catch {
      // silent
    } finally {
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
      <Grid container spacing={3}>
        {/* Left: Profile card */}
        <Grid item lg={4} md={5} xs={12}>
          <AccountProfile
            user={user}
            photoPreview={profile.preview}
            onPhotoChange={HandlePhoto}
            onPhotoUpload={UploadPhoto}
            hasNewPhoto={!!profile.preview}
          />
        </Grid>

        {/* Right: Forms */}
        <Grid item lg={8} md={7} xs={12}>
          <Stack spacing={3}>
            {/* Profile Info Card */}
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader
                avatar={<PersonOutlineIcon color="primary" />}
                title={
                  <Typography variant="h6" fontWeight={700}>
                    Profile Details
                  </Typography>
                }
                subheader="Update your personal information"
                sx={{ pb: 1 }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2.5}>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={user.name}
                      onChange={handleChange}
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Status"
                      name="status"
                      value={user.status}
                      onChange={handleChange}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      value={user.email}
                      onChange={handleChange}
                      size="small"
                      disabled
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="mobile"
                      type="number"
                      value={user.mobile}
                      onChange={handleChange}
                      size="small"
                      disabled
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Area / City"
                      name="city"
                      value={user.city}
                      onChange={handleChange}
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      name="dob"
                      type="date"
                      value={user.dob}
                      onChange={handleChange}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained"
                  onClick={HandleUpdate}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Save Changes
                </Button>
              </Box>
            </Card>

            {/* Password Change Card */}
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader
                avatar={<LockOutlinedIcon color="primary" />}
                title={
                  <Typography variant="h6" fontWeight={700}>
                    Change Password
                  </Typography>
                }
                subheader="You will be logged out after changing your password"
                sx={{ pb: 1 }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      name="old_password"
                      type={showPw.old ? 'text' : 'password'}
                      value={pwData.old_password}
                      onChange={handlePasswordChange}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPw((p) => ({ ...p, old: !p.old }))}
                              edge="end"
                            >
                              {showPw.old
                                ? <VisibilityOffOutlinedIcon fontSize="small" />
                                : <VisibilityOutlinedIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      name="new_password"
                      type={showPw.new ? 'text' : 'password'}
                      value={pwData.new_password}
                      onChange={handlePasswordChange}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPw((p) => ({ ...p, new: !p.new }))}
                              edge="end"
                            >
                              {showPw.new
                                ? <VisibilityOffOutlinedIcon fontSize="small" />
                                : <VisibilityOutlinedIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      name="confrim_password"
                      type={showPw.confirm ? 'text' : 'password'}
                      value={pwData.confrim_password}
                      onChange={handlePasswordChange}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPw((p) => ({ ...p, confirm: !p.confirm }))}
                              edge="end"
                            >
                              {showPw.confirm
                                ? <VisibilityOffOutlinedIcon fontSize="small" />
                                : <VisibilityOutlinedIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={updatePassword}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Update Password
                </Button>
              </Box>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};
